import { useState, useEffect, useCallback } from 'react'
import api from '../../services/api'
import StatsSummary from './components/StatsSummary'
import DailyChart from './components/DailyChart'
import TopPages from './components/TopPages'
import './Analytics.css'

interface StatsData {
  total_views: number
  unique_visitors: number
  avg_daily_views: number
}

interface DailyData {
  date: string
  views: number
  unique_visitors: number
}

interface TopPageData {
  page_path: string
  views: number
  unique_visitors: number
}

type Period = '7d' | '30d' | '90d'

const PERIOD_LABELS: Record<Period, string> = {
  '7d': '7 jours',
  '30d': '30 jours',
  '90d': '90 jours',
}

const PERIOD_DAYS: Record<Period, number> = {
  '7d': 7,
  '30d': 30,
  '90d': 90,
}

function Analytics() {
  const [period, setPeriod] = useState<Period>('30d')
  const [stats, setStats] = useState<StatsData | null>(null)
  const [dailyData, setDailyData] = useState<DailyData[]>([])
  const [topPages, setTopPages] = useState<TopPageData[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchData = useCallback(async (selectedPeriod: Period) => {
    setLoading(true)
    setError(null)

    try {
      const [statsResult, dailyResult, topPagesResult] = await Promise.all([
        api.get<StatsData>(`/pv/stats?period=${selectedPeriod}`),
        api.get<DailyData[]>(`/pv/daily?days=${PERIOD_DAYS[selectedPeriod]}`),
        api.get<TopPageData[]>(`/pv/top-pages?period=${selectedPeriod}&limit=10`),
      ])

      setStats(statsResult)
      setDailyData(dailyResult)
      setTopPages(topPagesResult)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement des statistiques')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchData(period)
  }, [period, fetchData])

  const handlePeriodChange = (newPeriod: Period) => {
    setPeriod(newPeriod)
  }

  return (
    <div className="analytics">
      <div className="analytics-header">
        <div>
          <h1>Statistiques</h1>
          <p className="analytics-subtitle">Suivi des visites du site public</p>
        </div>
        <div className="period-selector">
          {(Object.keys(PERIOD_LABELS) as Period[]).map((p) => (
            <button
              key={p}
              className={`period-btn ${period === p ? 'active' : ''}`}
              onClick={() => handlePeriodChange(p)}
            >
              {PERIOD_LABELS[p]}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          <div>{error}</div>
        </div>
      )}

      <StatsSummary
        totalViews={stats?.total_views ?? 0}
        uniqueVisitors={stats?.unique_visitors ?? 0}
        avgDailyViews={stats?.avg_daily_views ?? 0}
        loading={loading}
      />

      <div className="analytics-grid">
        <DailyChart data={dailyData} loading={loading} />
        <TopPages data={topPages} loading={loading} />
      </div>
    </div>
  )
}

export default Analytics
